import { apiClient } from './client';
import type { Subscriber, CreateSubscriberInput } from '../types';

export interface SubscriberPage {
  items: Subscriber[];
  total: number;
  page: number;
  limit: number;
}

export async function getSubscribers(params?: {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
}): Promise<SubscriberPage> {
  const { data } = await apiClient.get<SubscriberPage>('/api/subscribers', { params });
  return data;
}

export async function getSubscriber(id: number): Promise<Subscriber> {
  const { data } = await apiClient.get<Subscriber>(`/api/subscribers/${id}`);
  return data;
}

export async function createSubscriber(payload: CreateSubscriberInput, idempotencyKey?: string): Promise<Subscriber> {
  const { data } = await apiClient.post<Subscriber>('/api/subscribers', payload, {
    headers: idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : undefined,
  });
  return data;
}

export async function updateSubscriber(
  id: number,
  payload: Partial<CreateSubscriberInput> & { updated_at?: string },
): Promise<Subscriber> {
  const { data } = await apiClient.put<Subscriber>(`/api/subscribers/${id}`, payload);
  return data;
}

export async function deleteSubscriber(id: number): Promise<void> {
  await apiClient.delete(`/api/subscribers/${id}`);
}

/** Operators extend by `days`; resellers buy one term of `package_id` (default: the line's package). */
export async function renewSubscriber(
  id: number,
  body: { days?: number; package_id?: number },
  idempotencyKey?: string,
): Promise<Subscriber> {
  const { data } = await apiClient.post<Subscriber>(`/api/subscribers/${id}/renew`, body, {
    headers: idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : undefined,
  });
  return data;
}

export interface RenewalQuote {
  id: number;
  username: string;
  package_name?: string;
  exp_date: string | null;
  price: number;
  error?: string;
}

export interface RenewalPreview {
  lines: RenewalQuote[];
  renewable: number;
  total: number;
  balance: number;
  balance_after: number;
  truncated?: boolean;
}

export interface RenewalLineResult {
  id: number;
  username: string;
  status: 'renewed' | 'already_renewed' | 'failed';
  exp_date?: string;
  price?: number;
  error?: string;
}

export interface BulkRenewalResult {
  lines: RenewalLineResult[];
  renewed: number;
  failed: number;
  spent: number;
  balance: number;
}

/** A reseller's batch priced line by line, soonest expiry first. Charges nothing. */
export async function previewRenewals(sel: { ids: number[] } | { scope: 'expiring' | 'lapsed' }): Promise<RenewalPreview> {
  const { data } = await apiClient.post<RenewalPreview>('/api/subscribers/renew-preview', sel);
  return data;
}

/** Renews a reseller's batch on the lines' own packages. The key makes a retry free. */
export async function bulkRenew(ids: number[], idempotencyKey: string): Promise<BulkRenewalResult> {
  const { data } = await apiClient.post<BulkRenewalResult>('/api/subscribers/bulk-renew', { ids }, {
    headers: { 'Idempotency-Key': idempotencyKey },
  });
  return data;
}

export async function bulkSubscribers(
  ids: number[],
  action: 'suspend' | 'unsuspend' | 'delete' | 'renew',
  days?: number,
): Promise<{ affected: number }> {
  const { data } = await apiClient.post<{ affected: number }>('/api/subscribers/bulk', { ids, action, days });
  return data;
}

export async function getSubscriberIDs(params?: { search?: string; status?: string }): Promise<number[]> {
  const { data } = await apiClient.get<{ ids: number[] }>('/api/subscribers/ids', { params });
  return data.ids;
}

export async function revokeSubscriberLinks(id: number): Promise<{ username: string; password: string }> {
  const { data } = await apiClient.post<{ username: string; password: string }>(`/api/subscribers/${id}/revoke-links`);
  return data;
}
